/**
 * Button — the primary action control. Four variants (primary = the single
 * dusty-rose CTA, secondary = raised paper, ghost = quiet, danger = soft coral)
 * and three sizes. Supports a loading state (swaps content for a Spinner while
 * keeping width) and optional leading / trailing lucide icons.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Spinner } from "./Spinner.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual emphasis. @default "primary" */
  readonly variant?: ButtonVariant;
  /** Control height. @default "md" */
  readonly size?: ButtonSize;
  /** When true, shows a spinner and blocks interaction. */
  readonly loading?: boolean;
  /** Icon rendered before the label (a lucide element). */
  readonly leadingIcon?: ReactNode;
  /** Icon rendered after the label (a lucide element). */
  readonly trailingIcon?: ReactNode;
}

const SPINNER_SIZE = { sm: "sm", md: "sm", lg: "md" } as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  const classes = [
    "lc-btn",
    `lc-btn--${variant}`,
    `lc-btn--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled ?? loading}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {leadingIcon ? <span className="lc-btn__icon">{leadingIcon}</span> : null}
      <span className="lc-btn__label">{children}</span>
      {trailingIcon ? <span className="lc-btn__icon">{trailingIcon}</span> : null}
      {loading ? (
        <span className="lc-btn__spinner" aria-hidden="true">
          <Spinner size={SPINNER_SIZE[size]} />
        </span>
      ) : null}
    </button>
  );
});
